import type { MedplumClient } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import {
  MISSING_NOTE_TASK_CODE,
  TASK_TYPE_SYSTEM,
  TREATMENT_PLAN_DUE_TASK_CODE,
  TREATMENT_PLAN_RENEWAL_TASK_CODE,
} from '@unify/fhir-model/constants';
import { getTaskTypeCoding } from '../pages/tasks/services/taskFhirMapping';
import {
  APPOINTMENT_REQUEST_TASK,
} from '../pages/client-portal/utils/appointmentRequestTask';
import {
  APPOINTMENT_RESCHEDULE_TASK,
} from '../pages/client-portal/utils/rescheduleRequestTask';

export interface ProviderTaskDestination {
  path: string;
  scrollToMessageId?: string;
}

function taskTypeCode(task: Task): string | undefined {
  return getTaskTypeCoding(task)?.code;
}

export function communicationIdFromTask(task: Task): string | undefined {
  const ref = task.reasonReference?.reference;
  if (!ref?.startsWith('Communication/')) return undefined;
  return ref.slice('Communication/'.length);
}

export function appointmentIdFromTaskFocus(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('Appointment/')) return undefined;
  return ref.slice('Appointment/'.length);
}

export function episodeOfCareIdFromTaskFocus(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return ref.slice('EpisodeOfCare/'.length);
}

function patientIdFromTask(task: Task): string | undefined {
  const ref = task.for?.reference;
  if (!ref?.startsWith('Patient/')) return undefined;
  return ref.slice('Patient/'.length);
}

function isPortalAppointmentTask(task: Task): boolean {
  const code = taskTypeCode(task);
  return code === APPOINTMENT_REQUEST_TASK.code || code === APPOINTMENT_RESCHEDULE_TASK.code;
}

function isMissingNoteTask(task: Task): boolean {
  return task.code?.coding?.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === MISSING_NOTE_TASK_CODE,
  ) ?? false;
}

function isTreatmentPlanTask(task: Task): boolean {
  return (
    task.code?.coding?.some(
      (c) =>
        c.system === TASK_TYPE_SYSTEM &&
        (c.code === TREATMENT_PLAN_DUE_TASK_CODE || c.code === TREATMENT_PLAN_RENEWAL_TASK_CODE),
    ) ?? false
  );
}

async function resolvePortalAppointmentTaskDestination(
  medplum: MedplumClient,
  task: Task,
): Promise<ProviderTaskDestination | null> {
  const communicationId = communicationIdFromTask(task);
  if (communicationId) {
    try {
      const message = await medplum.readResource('Communication', communicationId);
      const threadRef = message.partOf?.[0]?.reference;
      const threadId = threadRef?.startsWith('Communication/')
        ? threadRef.slice('Communication/'.length)
        : message.id;
      if (threadId) {
        return {
          path: `/dashboard/messages/${threadId}`,
          scrollToMessageId: communicationId,
        };
      }
    } catch {
      // fall through to calendar
    }
  }

  const appointmentId = appointmentIdFromTaskFocus(task);
  if (appointmentId) {
    return { path: `/dashboard/calendar?eventId=${appointmentId}` };
  }

  return null;
}

/** Where a provider should land to action an open Task (messages, calendar, chart, etc.). */
export async function resolveProviderTaskDestination(
  medplum: MedplumClient,
  task: Task,
): Promise<ProviderTaskDestination | null> {
  if (isPortalAppointmentTask(task)) {
    return resolvePortalAppointmentTaskDestination(medplum, task);
  }

  if (isMissingNoteTask(task)) {
    const appointmentId = appointmentIdFromTaskFocus(task);
    if (appointmentId) {
      return { path: `/dashboard/appointments/${appointmentId}` };
    }
  }

  if (isTreatmentPlanTask(task)) {
    const patientId = patientIdFromTask(task);
    const episodeId = episodeOfCareIdFromTaskFocus(task);
    if (patientId && episodeId) {
      const params = new URLSearchParams({ tab: 'chart', episodeId });
      return { path: `/dashboard/clients/${patientId}?${params.toString()}` };
    }
    if (patientId) {
      return { path: `/dashboard/clients/${patientId}?tab=chart` };
    }
  }

  const patientRef = task.for?.reference;
  if (patientRef?.startsWith('Patient/')) {
    const patientId = patientRef.slice('Patient/'.length);
    if (task.focus?.reference?.startsWith('Questionnaire/') && task.id) {
      return { path: `/dashboard/clients/${patientId}/measures/assessment/${task.id}` };
    }
  }

  return null;
}

export function buildProviderTaskNavigationUrl(destination: ProviderTaskDestination): string {
  if (!destination.scrollToMessageId) return destination.path;
  const params = new URLSearchParams({ messageId: destination.scrollToMessageId });
  return `${destination.path}?${params.toString()}`;
}
